/**
 * Cheerful message generation utility
 *
 * Generates user-friendly messages to display after successful searches,
 * incorporating location details and search parameters.
 *
 * Feature 060: Removed miles parameter - now only mentions county coverage
 */

/**
 * Parameters for generating cheerful messages
 */
type CheerfulMessageParams = {
  zipCode?: string;
  county?: string;
  isViewingHistoryResults: boolean;
  searchCounter: number;
};

/**
 * Generates a random cheerful message based on search parameters
 *
 * Returns a user-friendly message describing the search that was performed.
 * If viewing history results, returns a simple message indicating history view.
 * Otherwise, randomly selects from a pool of messages incorporating location details.
 *
 * @param params - Message generation parameters including location and search info
 * @returns Formatted cheerful message string
 */
export const generateCheerfulMessage = ({
  zipCode,
  county,
  isViewingHistoryResults,
}: CheerfulMessageParams): string => {
  if (isViewingHistoryResults) {
    return 'You are viewing results from your prompt history.';
  }

  const zip = zipCode || 'your area';
  const countyText = county || 'the local area';

  const cheerfulMessages = [
    `We searched providers around ZIP ${zip} in ${countyText} so you can confidently review the best options.`,
    `Great news! We explored providers in ${countyText} near ZIP ${zip} to bring you these carefully matched providers.`,
    `Success! We've scanned ${countyText} around ZIP ${zip} to find you these excellent matches.`,
    `Here's what we found! Providers in ${countyText}, near ZIP ${zip}, ready for your review.`,
    `We're pleased to present providers serving ${countyText} around ZIP ${zip} for your convenience.`,
    `Wonderful! We've searched around ZIP ${zip} in ${countyText} to bring you these quality matches.`,
    `Perfect! These providers serve ${countyText} near ZIP ${zip}—review your top matches below.`,
    `Excellent! We found providers near ZIP ${zip} in ${countyText}, tailored to your needs.`,
  ];

  const randomIndex = Math.floor(Math.random() * cheerfulMessages.length);
  return cheerfulMessages[randomIndex];
};

